import { AsyncPipe, DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { listUserNamesWithEmailQuery, User } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';
import { Utils } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, IconComponent, DatePipe, AsyncPipe, NgTemplateOutlet, TooltipDirective],
  selector: 'aux-file-viewer',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  templateUrl: './file-viewer.component.html',
})
export class FileViewerComponent implements OnInit {
  private authQuery = inject(AuthQuery);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  @Input() disableFirstFileMargin = false;
  @Input() fileManager: FileManagerComponent | undefined;
  files$: Observable<File[]> | undefined;
  loading$: Observable<boolean> | undefined;
  @Input() onlyShowUploaded = true;
  @Output() removeFileChange = new EventEmitter<File>();
  @Input() removeOnlyFromStore = false;
  @Input() showUserAndDate = false;
  @Input() type?: 'edc' | 'inAppBudget' | undefined = undefined;
  users = new Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>();

  private loggedInUser = this.authQuery.user();

  ngOnInit(): void {
    if (this.fileManager) {
      this.files$ = this.fileManager.fileQuery.selectAll({
        filterBy: (entity) => (this.onlyShowUploaded ? entity.uploaded : true),
      });
      this.loading$ = this.fileManager.fileQuery.selectLoading();
    }
  }

  constructor() {
    this.mainQuery
      .select('userList')
      .pipe(takeUntilDestroyed())
      .subscribe((_users) => {
        _users.forEach((user: listUserNamesWithEmailQuery) => {
          this.users.set(user.sub, user);
        });
      });
  }

  getloggedInUserSub() {
    return this.loggedInUser.sub;
  }

  onDownload(file: File) {
    if (file) {
      if (!file.rawFile) {
        const fileSuccess = this.fileManager?.apiService.downloadFileFromPath(
          file.bucket_key,
          file.fileName
        );

        if (!fileSuccess) {
          this.overlayService.error('There was a problem downloading the file!');
        }
      } else {
        const newBlob = new Blob([file.rawFile]);

        this.fileManager?.apiService.downloadBlob(newBlob, file.fileName);
      }
    } else {
      this.overlayService.error('There was a problem downloading the file!');
    }
  }

  onRemove(file: File) {
    this.overlayService.openPopup<
      { message: string },
      boolean | undefined,
      ConfirmationModalComponent
    >({
      content: ConfirmationModalComponent,
      data: {
        message: `Are you sure you want to remove this document?`,
      },
      settings: {
        header: 'Remove Document',
        primaryButton: {
          action: (instance) => {
            if (this.type === 'edc') {
              this.fileManager?.removeFileSoft(file);
              instance?.ref.close();

              return;
            }

            if (!this.removeOnlyFromStore) {
              const success = this.fileManager?.removeFile(file);

              if (success) {
                this.overlayService.success(`${file.fileName} removed!`);
              }

              instance?.ref.close();

              return;
            }

            this.fileManager?.fileService.removeFromStore(file);
            this.fileManager?.resetUploadInput();
            this.removeFileChange.emit(file);
            instance?.ref.close();
          },
          label: 'Remove',
        },
      },
    });
  }

  userFormatter(sub: string | undefined) {
    const user = this.users.get(sub || '');

    if (user) {
      const isUserAuxAdmin = user.email.includes('@auxili.us');

      if (this.authQuery.isAuxAdmin() || !isUserAuxAdmin) {
        return `${user.given_name} ${user.family_name}`;
      }

      return 'Auxilius Expert';
    }

    return Utils.zeroHyphen;
  }
}
